import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Phone, Menu, X, ExternalLink } from 'lucide-react';
import { LOGOS, CONTACT, NAV } from '../lib/constants';
import { Button } from './ui/Button';
import { ContactEmail } from './ui/ContactEmail';
function navHref(label: string) {
  if (['Managed Services', 'Security', 'Hosting', 'Voice', 'Service overview'].includes(label)) return '#services';
  if (['Legal', 'Accounting', 'Engineering', 'Health'].includes(label)) return '#industries';
  if (['Our Difference', 'Our Own Infrastructure'].includes(label)) return '#difference';
  if (label === 'Our Story') return '#leadership';
  if (label === 'The Independent IT Review') return '#it-review';
  if (['Case Studies', 'Testimonials'].includes(label)) return '#outcomes';
  if (label === 'FAQ') return '#faq';
  return 'https://ctgroup.com.au/media-events/';
}
function UtilityBar() {
  return <div className="hidden w-full bg-navy text-white lg:block" data-id="element-65" data-mp="3fb891rnay5ln">
      <div className="mx-auto flex max-w-content items-center justify-between px-8 py-2 text-[13px]" data-id="element-66" data-mp="3f9nbru9ls8k3">
        <p className="text-white/80" data-id="element-67" data-mp="2wihxfwura8jq">
          <span className="font-semibold text-ct-light" data-id="element-68" data-mp="2rli1wfvj8o6v">
            Free Independent IT Review
          </span>
          <span className="text-white/60" data-id="element-69" data-mp="2mnpyoppftfnd">
            {' '}
            | clarity before you make a change.
          </span>
        </p>
        <div className="flex items-center gap-6" data-id="element-70" data-mp="2hpoiqdnb5wg4">
          <a href="https://solutions.ctgroup.com.au/portal" className="flex items-center gap-1.5 text-white/85 transition-colors hover:text-white" data-id="element-71" data-mp="3fblu7r39t1ik">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden data-id="element-72" data-mp="35l0xk975q72a" />
            Client Portal
          </a>
          <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 text-white/85 transition-colors hover:text-white" data-id="element-73" data-mp="30mzhlx512nv1">
            <Phone className="h-3.5 w-3.5" aria-hidden data-id="element-74" data-mp="1yinvvhn1cd5" />
            {CONTACT.phone}
          </a>
          <ContactEmail label="Email CT Group" className="min-h-0 text-[13px] text-white/85 hover:text-white" iconClassName="h-3.5 w-3.5" data-id="element-75" data-mp="3t90ob94gxxgv" />
        </div>
      </div>
    </div>;
}
function DesktopNav() {
  const [open, setOpen] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const enter = (label: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(label);
  };
  const leave = () => {
    closeTimer.current = window.setTimeout(() => setOpen(null), 120);
  };
  const focusFirstItem = (button: HTMLButtonElement, label: string) => {
    setOpen(label);
    window.requestAnimationFrame(() => button.parentElement?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus());
  };
  const handleMenuKey = (event: React.KeyboardEvent<HTMLDivElement>, label: string) => {
    const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]'));
    const current = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (current + 1) % links.length : (current - 1 + links.length) % links.length;
      links[next]?.focus();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(null);
      event.currentTarget.parentElement?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  };
  return <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex" data-id="element-76" data-mp="10632kewxlfbh">
      {NAV.map(item => <div key={item.label} className="relative" onMouseEnter={() => enter(item.label)} onMouseLeave={leave} data-id="element-77" data-mp="1f3253ocxehz3">
          <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-[15px] font-semibold text-ink transition-colors hover:text-ct-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ct-cyan" aria-expanded={open === item.label} aria-haspopup="true" onFocus={() => enter(item.label)} onKeyDown={event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusFirstItem(event.currentTarget, item.label);
        }
        if (event.key === 'Escape') setOpen(null);
      }} data-id="element-78" data-mp="1a50kv03rkcm1">
            {item.label}
            <ChevronDown className={`h-4 w-4 transition-transform ${open === item.label ? 'rotate-180' : ''}`} aria-hidden data-id="element-79" data-mp="1k85yhy3g4u0u" />
          </button>
          {open === item.label && <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-card border border-border bg-white p-2 shadow-xl shadow-navy/10" role="menu" aria-label={`${item.label} menu`} onKeyDown={event => handleMenuKey(event, item.label)} data-id="element-80" data-mp="1t8c5r28jwxrk">
              {item.children.map(c => <a key={c.label} href={navHref(c.label)} role="menuitem" onClick={() => setOpen(null)} className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[14px] font-medium text-ink transition-colors hover:bg-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ct-cyan" data-id="element-81" data-mp="15h6smutzwjky">
                  {c.accent && <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{
            background: c.accent
          }} aria-hidden data-id="element-82" data-mp="f162aytf6hv0" />}
                  <span className={c.accent ? '' : 'ml-[calc(0.625rem+0.625rem)]'} data-id="element-83" data-mp="19hpetbcjpcz">
                    {c.label}
                  </span>
                </a>)}
            </div>}
        </div>)}
    </nav>;
}
function MobileMenu({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return <div className={`fixed inset-0 z-[60] lg:hidden ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open} data-id="element-84" data-mp="3it86aaba11mk">
      <div className={`absolute inset-0 bg-navy/50 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} data-id="element-85" data-mp="33wqvbk701p0j" />
      <div className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-white shadow-2xl transition-transform duration-300 ease-smooth ${open ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-label="Menu" data-id="element-86" data-mp="dnxm0fuxn8l">
        <div className="flex items-center justify-between border-b border-border px-5 py-4" data-id="element-87" data-mp="3mrxt0ti7fviv">
          <img src={LOGOS.main} alt="CT Group" className="h-8" data-id="element-88" data-mp="3rqs93nosf5za" />
          <button onClick={onClose} aria-label="Close menu" className="flex h-11 w-11 items-center justify-center rounded-lg text-ink hover:bg-mist" data-id="element-89" data-mp="jc9x5avq76ae">
            <X className="h-6 w-6" aria-hidden data-id="element-90" data-mp="28sw0o8556p8k" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-91" data-mp="23vvvdzg49qii">
          {NAV.map(item => <div key={item.label} className="border-b border-border/70" data-id="element-92" data-mp="2iki7lsag625i">
              <button className="flex w-full items-center justify-between py-3.5 text-[16px] font-bold text-ink" onClick={() => setExpanded(expanded === item.label ? null : item.label)} aria-expanded={expanded === item.label} aria-haspopup="menu" data-id="element-93" data-mp="2dkx8nhva1tss">
                {item.label}
                <ChevronDown className={`h-5 w-5 transition-transform ${expanded === item.label ? 'rotate-180' : ''}`} aria-hidden data-id="element-94" data-mp="1ytoecvgfjk9b" />
              </button>
              {expanded === item.label && <div className="pb-3" data-id="element-95" data-mp="368jfcyzsxejv">
                  {item.children.map(c => <a key={c.label} href={navHref(c.label)} onClick={onClose} className="flex items-center gap-2.5 py-2.5 pl-1 text-[15px] text-slate" data-id="element-96" data-mp="13np629qvg73v">
                      {c.accent && <span className="h-2 w-2 rounded-full" style={{
                background: c.accent
              }} aria-hidden data-id="element-97" data-mp="1icvbwgk7svkq" />}
                      {c.label}
                    </a>)}
                </div>}
            </div>)}
        </div>
        <div className="border-t border-border px-5 py-4" data-id="element-98" data-mp="218ldw3s5drym">
          <Button as="a" href="#it-review" variant="primary" full data-id="element-99" data-mp="1ylmrk85f2su">
            Book your free IT Review
          </Button>
        </div>
      </div>
    </div>;
}
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <header className="sticky top-0 z-50 w-full" data-id="element-100" data-mp="26dowrjl1klm4">
      <UtilityBar data-id="element-101" data-mp="334r2g3p3yit2" />
      <div className={`w-full border-b transition-[background-color,border-color,box-shadow] duration-200 ease-out ${scrolled ? 'border-border bg-white/90 shadow-sm backdrop-blur-md' : 'border-border/60 bg-white'}`} data-id="element-102" data-mp="334zjm7s9kaa2">
        <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-5 py-3 md:px-8" data-id="element-103" data-mp="2p5ztevp3rnbs">
          <a href="#top" className="flex-shrink-0" aria-label="CT Group home" data-id="element-104" data-mp="1ipnu2rajnzem">
            <img src={LOGOS.main} alt="CT Group" className="h-9 md:h-10" data-id="element-105" data-mp="1inkubfjlarwp" />
          </a>

          <DesktopNav data-id="element-106" data-mp="1dsxe03yqp3t7" />

          <div className="hidden lg:flex" data-id="element-107" data-mp="1d2xhvl2bske0">
            <Button as="a" href="#it-review" variant="primary" data-id="element-108" data-mp="1d3pdgvvxai4a">
              Free IT Review
            </Button>
          </div>

          <button className="flex h-11 w-11 items-center justify-center rounded-lg text-ink hover:bg-mist lg:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)} data-id="element-109" data-mp="181q8xiog5f5r">
            <Menu className="h-6 w-6" aria-hidden data-id="element-110" data-mp="1vt5dzj7lyakp" />
          </button>
        </div>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} data-id="element-111" data-mp="1qucamjm1bzy9" />
    </header>;
}